/* ============================================================================
   slider.js — <nko-slider>, die Karussells der Startseite
   ----------------------------------------------------------------------------
   Geladen als <script type="module"> von sections/slideshow,
   sections/featured, blocks/featured und blocks/blog. Ersetzt nko-slider.js
   (Embla samt Fade- und Autoplay-Plugin, 29 KB roh / 11 KB br).

   Die Spur ist CSS-Scroll-Snap (assets/component-slider.css) und ohne dieses
   Skript wischbar; die Fade-Slideshow zeigt dann ihre erste Folie. Hier steht
   nur, was CSS nicht kann:
   - Pfeile: scrollen zum naechsten Einrastpunkt (Folie mittig, am Rand
     gekappt, wie vorher Embla); gesperrt am Anfang bzw. Ende der Reihe,
     gemessen per IntersectionObserver auf der ersten und letzten Folie (im
     Raster sind beide ganz sichtbar, also beide gesperrt).
   - Wischbremse: ein Wisch (Touch) rastet wie bei Embla genau eine Karte
     weiter ein, auch wo der Browser `scroll-snap-stop` im Schwung nicht
     einhaelt (Chromium). Nicht auf iOS/iPadOS: WebKit haelt es selbst ein,
     und die Bremse spulte den Streifen dort auf die erste Karte zurueck.
   - Slideshow (hat `data-slider-transition`): genau eine Folie ist aktiv,
     alle anderen bekommen `inert`. Punkte waehlen die Folie. Im Fade wechselt
     ein Wisch sie nur mit Schwung oder weitem Zug, wie bei Embla.
   - Autoplay: nur mit `data-slider-autoplay="true"` und mehr als einer
     Folie. Der Takt ist die CSS-Animation des Fortschrittsbalkens —
     `animationend` blaettert weiter, am Ende zurueck zur ersten Folie. So
     sind Anhalten und Fortsetzen reines `animation-play-state` (Hover und
     Tastaturfokus direkt im CSS), und der Balken kann nie vom Takt abweichen.
     Bei `prefers-reduced-motion: reduce` startet es NICHT von selbst; der
     Pause-Knopf kann es dann ausdruecklich starten.
   - Theme-Editor: `shopify:block:select` springt zur Folie des gewaehlten
     Blocks und haelt an, `shopify:block:deselect` laeuft weiter.

   Die Einstellung `data-slider-loop` wird nicht mehr gelesen: Embla
   verschob fuer die Endlosschleife Folien per transform, Scroll-Snap kann das
   nicht. Autoplay springt am Ende zurueck, Streifen ohne Autoplay enden an
   der letzten Folie (Schema-Hinweis am Setting).

   Kein Konstruktor, Aufbau in connectedCallback, Abbau ueber EINEN
   AbortController: im Editor ersetzt ein Neurendern das Element.
   ========================================================================== */

const ruhig = matchMedia("(prefers-reduced-motion: reduce)");
/* iOS-/iPadOS-WebKit (jeder Browser dort): nur er kennt die Eigenschaft.
   Dort bleibt die Wischbremse aus, Begruendung an ihrem Kommentar. */
const ios = CSS.supports("-webkit-touch-callout", "none");

class NkoSlider extends HTMLElement {
  connectedCallback() {
    this.vp = this.querySelector(".slider__viewport");
    if (!this.vp) return;

    const prev = this.querySelector(".slider__prev");
    const next = this.querySelector(".slider__next");
    // Slideshow: eine Folie sichtbar, die anderen inert
    this.one = "sliderTransition" in this.dataset;
    this.fade = this.dataset.sliderTransition === "fade";

    this.ac = new AbortController();
    const on = (el, t, fn) => el?.addEventListener(t, fn, { signal: this.ac.signal });

    on(prev, "click", () => this.step(-1));
    on(next, "click", () => this.step(1));
    on(this, "click", (e) => {
      const p = e.target.closest("[data-slide-index]");
      if (p) this.go(+p.dataset.slideIndex);
      if (this.auto && e.target.closest("[data-slider-pause]")) this.play(this.stopped);
    });
    // Theme-Editor: zur Folie des gewaehlten Blocks, solange angehalten
    on(this, "shopify:block:select", (e) => {
      const n = this.slides().findIndex((t) => t.contains(e.target));
      if (n > -1) this.state((this.editor = true)), this.go(n);
    });
    on(this, "shopify:block:deselect", () => this.state((this.editor = false)));

    const s = this.slides();
    if (!s[0]) return;
    if (this.one) this.show(0);

    if (this.one && s[1] && this.dataset.sliderAutoplay === "true") {
      this.auto = true;
      on(this, "animationend", (e) => {
        if (e.animationName === "slider-progress") this.go(this.index + 1);
      });
      on(document, "visibilitychange", () => this.state());
      this.play(!ruhig.matches);
    }
    /* FADE: wischen wie vorher mit Embla (die Zelle scrollt nicht; CSS gibt
       senkrechte Gesten per `touch-action: pan-y` an die Seite ab).
       Die Folie wechselt nur bei einem Schwung oder einem weiten Zug — ein
       langsamer, kurzer Zug rastet zurueck (E1-05; vorher genuegten 40 px).
       Emblas Regel (DragTracker + allowedForce), gegen LIVE gemessen bei 390:
       - Schwung: Tempo im Messfenster (Beginn rueckt alle 170 ms nach; 170 ms
         ohne Bewegung vor dem Loslassen = kein Schwung, ebenso <= 0,1 px/ms),
         mal 400 ms (Maus 300) mindestens Breite/5, gekappt auf 50–225 px;
       - sonst ein Zug ueber 40 % der Breite (LIVE: 135 px bleibt, 165 px
         wechselt, bei 358 px Breite).
       p/w/l: Start, Beginn des Messfensters, letzter Punkt als [x, Zeit]. */
    if (this.fade) {
      let p, w, l;
      on(this.vp, "pointerdown", (e) => (p = w = l = [e.clientX, e.timeStamp]));
      on(this.vp, "pointercancel", () => (p = null));
      on(this.vp, "pointermove", (e) => {
        if (!p) return;
        l = [e.clientX, e.timeStamp];
        if (l[1] - w[1] > 170) w = l;
      });
      on(this.vp, "pointerup", (e) => {
        if (!p) return;
        const d = e.clientX - p[0],
          b = this.vp.clientWidth,
          t = e.timeStamp - w[1];
        let v = t && e.timeStamp - l[1] <= 170 ? (l[0] - w[0]) / t : 0;
        if (Math.abs(v) <= 0.1) v = 0;
        const f = v * (e.pointerType === "mouse" ? 300 : 400);
        const r = Math.abs(f) >= Math.min(Math.max(b / 5, 50), 225) ? f : Math.abs(d) > b * 0.4 ? d : 0;
        p = null;
        if (r && Math.abs(d) > 10) this.go(this.index - Math.sign(r));
      });
      return;
    }

    /* Tastaturfokus in einer Folie: dorthin scrollen wie Embla (`focus`, nur
       nach Tab). Der Browser selbst scrollt eine halb sichtbare Folie nicht
       nach. */
    on(this.vp, "focusin", (e) => {
      const t = e.target.closest(".slider__slide");
      if (t && e.target.matches(":focus-visible")) this.hin(this.mitte(t));
    });

    /* WISCHBREMSE: ein Wisch rastet wie bei Embla (`skipSnaps: false`)
       genau eine Karte weiter ein. `scroll-snap-stop: always`
       (component-slider.css) sollte das allein leisten, Chromium rollt mit
       Schwung aber darueber hinweg: 300 px Wisch bei 3000 px/s von Karte 0
       auf 2, LIVE auf 1 (per CDP erzeugte Touch-Geste, 390 px). Ein
       nachtraegliches scrollTo() bricht den Schwung dort nicht ab — er
       laeuft danach weiter.

       Darum entscheidet das Skript VOR dem Loslassen, wo es einrastet: solange
       der Finger liegt, ist nur EINE Karte Einrastpunkt (`scroll-snap-align`
       der anderen `none`), und der Browser landet mit jedem Schwung dort.
       Welche, entscheidet Emblas Regel (DragTracker + allowedForce):
       - schnell, also mehr als Breite/5 (gekappt auf 50–225 px) je 400 ms im
         Messfenster: die Nachbarkarte der Ausgangskarte in Wischrichtung,
         egal wie weit der Finger schon gezogen hat;
       - langsam: die Karte, die dem Punkt am naechsten steht, auf den der
         Restschwung zielt — Position plus Tempo mal 400 ms. Das Tempo misst
         bis zum Loslassen weiter und faellt dabei; nach 170 ms ohne
         Bewegung ist es null.
       Haelt der Finger an, kommt kein Scroll-Event mehr — der Wecker
       (`warte`) stellt dann rechtzeitig auf „langsam“ um und rechnet das
       fallende Tempo alle 30 ms nach.
       200 ms nach dem letzten Scrollen rasten wieder alle Karten ein (frei()).
       `touchend` statt `pointerup`: nach `pointercancel` (der Browser
       scrollt) kommt kein pointerup mehr. Nur Touch — ein Stift meldet kein
       touchend, die Bremse bliebe haengen. Maus, Pfeile und Tastatur bleiben
       aussen vor (hin()).

       NICHT AUF iOS/iPadOS (`ios`). WebKit haelt `scroll-snap-stop: always`
       dort im Schwung selbst ein (am Simulator: derselbe schnelle Wisch
       blaettert mit `always` eine Karte, mit `normal` zwei), die Bremse ist
       ueberfluessig — und sie machte die Streifen unbrauchbar: iOS-WebKit
       merkt sich den Einrastpunkt dem Verhalten nach als NUMMER in der
       Liste der Punkte. Unter der Bremse hat die Liste einen Eintrag
       (Nummer 0); gibt frei() die Karten wieder frei, rastet es neu auf
       Nummer 0 ein — auf der ersten Karte (mit zwei Punkten in der Liste
       landete es entsprechend auf der zweiten). Gemessen (iOS 27, iPadOS
       26.5): der Wisch landet richtig (scrollLeft 365), frei() setzt ihn im
       selben Moment auf 0. Dafuer genuegte schon senkrechtes Scrollen mit
       dem Finger auf dem Streifen (Karte 4 -> 1). In Chromium bleibt die
       Position bei frei() stehen. */
    const fang = (k) => {
      k = Math.max(0, Math.min(this.z.length - 1, k));
      if (k !== this.halt) s.forEach((t, j) => (t.style.scrollSnapAlign = this.zi[j] === k ? "" : "none"));
      this.halt = k;
    };
    const lahm = () => {
      const z = this.z,
        t = performance.now(),
        f = t - this.t1 > 170 ? 0 : ((this.x1 - this.x0) / Math.max(t - this.t0, 1)) * 400,
        x = this.vp.scrollLeft + f;
      fang(z.reduce((a, w, j) => (Math.abs(w - x) < Math.abs(z[a] - x) ? j : a), 0));
      if (f && this.finger) warte(lahm, 30);
    };
    const warte = (fn, ms) => (clearTimeout(this.uhr), (this.uhr = setTimeout(fn, ms)));
    on(this.vp, "pointerdown", (e) => {
      if (e.pointerType !== "touch" || ios) return;
      clearTimeout(this.uhr);
      const z = (this.z = this.ziele());
      // je Folie ihr Ziel; am Rand gekappte Folien teilen eines
      this.zi = s.map((t) => z.indexOf(this.mitte(t)));
      this.von = z.findIndex((x) => x > this.vp.scrollLeft - 20);
      this.schnell = Math.min(Math.max(this.vp.clientWidth / 5, 50), 225) / 400;
      this.t0 = this.t1 = performance.now();
      this.x0 = this.x1 = this.vp.scrollLeft;
      this.finger = true;
      this.halt = null;
      fang(this.von);
    });
    for (const t of ["touchend", "touchcancel"]) on(this.vp, t, () => ((this.finger = false), this.zi && warte(() => this.frei(), 200)));
    on(this.vp, "scroll", () => {
      if (!this.zi) return;
      if (!this.finger) return warte(() => this.frei(), 200);
      // Messfenster wie Emblas DragTracker: Beginn rueckt alle 170 ms nach
      const t = performance.now(),
        x = this.vp.scrollLeft;
      if (t - this.t0 > 170) (this.t0 = this.t1), (this.x0 = this.x1);
      this.t1 = t;
      this.x1 = x;
      // ms, bis das Tempo seit t0 unter die Schwelle faellt (<= 0: schon jetzt)
      const d = Math.abs(x - this.x0) / this.schnell - (t - this.t0);
      d > 0 ? fang(this.von + Math.sign(x - this.x0)) : lahm();
      warte(lahm, Math.min(d, 170));
    });

    /* Ganz sichtbar = Pfeil gesperrt; 0,98 statt 1 wegen Subpixel-Breiten.
       In der Slideshow ohne Fade wird die Folie aktiv, die mehr als halb im
       Bild steht. */
    this.io = new IntersectionObserver(
      (es) => {
        for (const { target: t, intersectionRatio: r } of es) {
          if (t === s[0] && prev) prev.disabled = r > 0.98;
          if (t === s.at(-1) && next) next.disabled = r > 0.98;
          if (this.one && r > 0.5) this.show(s.indexOf(t));
        }
      },
      { root: this.vp, threshold: [0.5, 0.99] }
    );
    for (const t of this.one ? s : [s[0], s.at(-1)]) this.io.observe(t);
  }

  disconnectedCallback() {
    this.ac?.abort();
    this.io?.disconnect();
    clearTimeout(this.uhr);
  }

  slides() {
    return [...this.querySelectorAll(".slider__slide")];
  }

  /* Folie n aktiv, alle anderen inert. Stand der Fokus in der alten Folie
     (Punkt oder Pause-Knopf per Tastatur), geht er auf dasselbe
     Bedienelement der neuen — jede Folie hat ihre eigene Punktreihe, und
     sonst fiele er auf <body> zurueck. */
  show(n) {
    const s = this.slides();
    const f = document.activeElement;
    const i = f.dataset?.slideIndex;
    /* Angehalten (Pause-Knopf) und dann gewechselt: der Balken der neuen
       Folie begaenne eingefroren bei 0 und saehe aus wie der inaktive — beide
       Punkte grau. Ohne `data-playing` zeigt das CSS den eigenen Punkt
       gefuellt wie ohne Autoplay; play() haengt die Animation wieder an, sie
       laeuft dann auf dieser Folie von vorn. Ohne Wechsel bleibt der Balken
       eingefroren, wo er stand. */
    if (this.stopped && n !== this.index) this.removeAttribute("data-playing");
    this.index = n;
    s.forEach((t, k) => (t.inert = k !== n));
    if (this.contains(f) && f.closest("[inert]")) s[n].querySelector(i ? `[data-slide-index="${i}"]` : "[data-slider-pause]")?.focus();
  }

  go(n) {
    const s = this.slides();
    n = (n + s.length) % s.length;
    this.fade ? this.show(n) : this.hin(this.mitte(s[n]));
  }

  /* Scrollposition, in der die Folie mittig steht, gekappt auf die Reihe —
     wie Embla (`align: center`, `containScroll: trimSnaps`) und wie das
     Einrasten im CSS. Auf ein paar Pixel genau reicht, der Browser rastet
     am naechsten Punkt ein. */
  mitte(t) {
    const v = this.vp,
      a = v.getBoundingClientRect(),
      b = t.getBoundingClientRect(),
      x = this.dataset.sliderAlign === "start" ? b.left - a.left - (parseFloat(getComputedStyle(v).scrollPaddingLeft) || 0) : b.left - a.left + (b.width - a.width) / 2;
    return Math.max(0, Math.min(v.scrollWidth - v.clientWidth, v.scrollLeft + x));
  }

  /* Die Einrast-Ziele aller Folien, gekappte gleiche nur einmal. */
  ziele() {
    return [...new Set(this.slides().map((t) => this.mitte(t)))];
  }

  /* Wischbremse loesen: alle Folien rasten wieder ein. */
  frei() {
    clearTimeout(this.uhr);
    if (!this.zi) return;
    this.zi = this.halt = null;
    for (const t of this.slides()) t.style.scrollSnapAlign = "";
  }

  /* Programmatisch scrollen (Pfeile, Punkte, Fokus). Vorher die Wischbremse
     loesen — tippt man kurz nach einem Wisch auf einen Pfeil, rastete das
     Ziel sonst auf der gebremsten Karte ein. */
  hin(left) {
    this.frei();
    this.vp.scrollTo({ left });
  }

  /* Ein Ziel weiter: von den Zielen aller Folien (ziele()) vom aktuellen
     aus einen Schritt. Aktuell ist das erste Ziel, das nicht klar links der
     Position liegt — eingerastet liegen Position und Ziel hoechstens einen
     halben Folienabstand (8 px) auseinander. Kurz nach
     einem Klick zaehlt dessen Ziel statt der Position mitten im weichen
     Scrollen — sonst schluckte ein schneller Doppelklick den zweiten
     Schritt. */
  step(d) {
    const z = this.ziele();
    const von = performance.now() - this.zeit < 500 ? this.ziel : this.vp.scrollLeft;
    const i = z.findIndex((x) => x > von - 20);
    this.zeit = performance.now();
    this.ziel = z[Math.max(0, Math.min(z.length - 1, i + d))];
    this.hin(this.ziel);
  }

  /* an = true: abspielen. Einmal gestartet bleibt die Animation angehaengt,
     Pause friert sie nur ein (component-slider.css) — ausser die Folie
     wechselt waehrend der Pause, siehe show(). */
  play(an) {
    this.stopped = !an;
    if (an) this.setAttribute("data-playing", "");
    this.state();
  }

  state() {
    this.toggleAttribute("data-paused", !!(this.stopped || this.editor || document.hidden));
    for (const b of this.querySelectorAll("[data-slider-pause]")) b.setAttribute("aria-pressed", !!this.stopped);
  }
}

if (!customElements.get("nko-slider")) customElements.define("nko-slider", NkoSlider);

/* NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer` —
   der Modulbereich ist der einzige Gueltigkeitsbereich dieser Datei. */
